/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { markModified, updateModel } from "data/store/reducers/model";
import { useAuth } from "cms/auth/hooks/use-auth";
import { setChangeNeeded, setReloadRequired } from "data/store/reducers/webpage";
import { waitAtLeast } from "utils/sleep/wait-at-least";
import { checkDiffs } from "utils/change-detection";

/**
 * @typedef {Parameters<typeof updateModel>[0]["update"]} ModelUpdates
 */

const useUpdateModel = () => {
    
    const dispatch = useDispatch();
    const { token } = useAuth();
    
    // Get current model state to compare changes
    const currentModel = useSelector((state) => state.model.current);

    const update = useCallback(
        /**
         * @param {ModelUpdates} update 
         * @param {boolean} reload 
         */
        async (update = {}, reload = false) => {
            
            // Check if there are actual changes before setting changeNeeded
            if (currentModel && Object.keys(update).length > 0) {
                const hasChanges = checkDiffs(currentModel, { ...currentModel, ...update }, "useUpdateModel");
                
                if (hasChanges) {
                    console.info(`[useUpdateModel] changes detected, setting changeNeeded to true`, update);
                    dispatch(setChangeNeeded(true));
                } else {
                    console.info(`[useUpdateModel] no changes detected, keeping changeNeeded as is`, update);
                }
            }
            
            // this dispatch will return a promise, event though the types are incorrect
            /** @type {ReturnType<ReturnType<typeof updateModel>>} */
            const asyncAction = dispatch(
                updateModel({
                    token,
                    update
                })
            );

            const actionResult = await waitAtLeast(asyncAction, 150);

            if (reload) {
                dispatch(setReloadRequired(true));
            }

            return actionResult;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token, currentModel]
    );

    const markModel = useCallback(
        async () => {

            const guid = currentModel?.guid;
            
            if (currentModel) {
                console.info(`[useUpdateModel] markModel modified ${guid}`);
                dispatch(setChangeNeeded(true));
            }

            // this dispatch will return a promise, even though the types are incorrect
            /** @type {ReturnType<ReturnType<typeof updateModel>>} */
            const asyncAction = dispatch(
                markModified({ token, guid })
            );

            const actionResult = await waitAtLeast(asyncAction, 150);

            // if (reload) {
            //     dispatch(setReloadRequired(true));
            // }

            return actionResult;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [token, currentModel]
    );

    return { update, markModel };
};

export { useUpdateModel };
